import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import {
  ChevronRight,
  Sparkles,
  BookOpen,
  Newspaper,
  MessageCircle,
  Compass,
} from 'lucide-react';

const TYPE_META = {
  story: { icon: BookOpen, className: 'bg-ruby/85 text-white' },
  article: { icon: Newspaper, className: 'bg-blue-500/85 text-white' },
  confession: { icon: MessageCircle, className: 'bg-purple-500/85 text-white' },
  series: { icon: Sparkles, className: 'bg-gold text-black' },
  episode: { icon: Sparkles, className: 'bg-gold text-black' },
  guide: { icon: Compass, className: 'bg-emerald-500/85 text-white' },
};

function ItemCard({ item }) {
  const meta = TYPE_META[item.content_type] || TYPE_META.story;
  const Icon = meta.icon;

  return (
    <Link
      to={item.href}
      className="group block rounded-xl overflow-hidden border border-border/60 hover:border-ruby/50 transition-all bg-surface"
      data-testid={`latest-${item.content_type}-${item.id}`}
    >
      <div className="relative aspect-[4/3] bg-gradient-to-br from-ruby/15 via-surface to-black overflow-hidden">
        {item.cover_image ? (
          <img
            src={item.cover_image}
            alt={item.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <Badge className={`absolute top-3 left-3 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${meta.className}`}>
          <Icon className="w-3 h-3" />
          {item.content_type_label}
        </Badge>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-foreground line-clamp-2 mb-1 group-hover:text-ruby transition-colors">
          {item.title}
        </h3>
        {item.excerpt && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {item.excerpt}
          </p>
        )}
      </div>
    </Link>
  );
}

/**
 * LatestContentSection — "Acabado de chegar" mixed feed just below the hero.
 * Silently renders nothing when the feed is empty (e.g. brand-new install).
 */
export default function LatestContentSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/homepage/latest?limit=12');
        if (!cancelled) setItems(data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section className="px-4 md:px-12 mt-10 md:mt-14" data-testid="latest-section-loading">
        <div className="max-w-6xl mx-auto">
          <div className="h-8 w-56 rounded bg-surface/60 animate-pulse mb-5" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl bg-surface/40 border border-border animate-pulse h-64" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0) return null;

  return (
    <section className="px-4 md:px-12 mt-10 md:mt-14" data-testid="latest-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-ruby" />
              Acabado de chegar
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              O que foi publicado recentemente — contos, séries, confissões e mais.
            </p>
          </div>
          <Link
            to="/browse"
            className="text-sm text-ruby hover:underline flex items-center gap-1 shrink-0"
          >
            Ver mais <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.slice(0, 8).map((item) => (
            <ItemCard key={`${item.content_type}-${item.id}`} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}
